/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { Fragment, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import * as _ from 'lodash'
import { ActivityRewardList } from '@views/MyPage/UserActivity/ActivityRewardList'
import dayjs from 'dayjs'
import { isMobile } from 'react-device-detect'
import { UserActivityScoreOption, UserPickWrittenActivityScoreOption } from '@interface/admin/common'
import { isProdEnv, isQaEnv, isTestEnv, toUserActivityTypeDesc } from '@utils/utils'

// img
import { ArrowIco } from '@shared/Icon/ArrowIcon'
// css
import { SelectMonth } from '@styles/button.style'
import { AdvButton } from '@components/Button'
import { UserActivityScoreWrap } from '@views/MyPage/index.style'
import PopupUtils from '@utils/popup-utils'
import useRootStore from '@store/useRootStore'
import { useOnUnmount } from '@utils/reactExt'
import { GlobalStoreKey } from '@v2/constants/common'
import { USER_ACTIVITY_TYPE } from '@v2/constants/activity'
import classNames from 'classnames'


export const UserActivityScore: React.FC = observer(() => {
  const { userStore, globalStore, chattingStore } = useRootStore()
  const { myActivityRewardHistory } = userStore
  const [month, setMonth] = useState<string>(dayjs().format('YYYY-MM'))

  useEffect(() => {
    userStore.fetchMyActivityRewardHistory(month).catch(console.error)
  }, [month])

  useOnUnmount(() => {
    userStore.setMyActivityRewardHistory(null)
  })

  const isPreviousButtonDisabled = () => {
    let activityStartMonth = dayjs('2023-07-01')
    if (isTestEnv()) {
      activityStartMonth = dayjs('2023-07-01')
    } else if (isQaEnv() || isProdEnv()) {
      activityStartMonth = dayjs('2023-08-01')
    }
    const oneYearAgo = dayjs().add(-11, 'months').startOf('month')
    return dayjs(month).add(-1, 'months').isBefore(activityStartMonth)
      || dayjs(month).isBefore(oneYearAgo)
  }

  const isNextButtonDisabled = () => dayjs().startOf('months').isSame(dayjs(month))

  // const onChangeMonth = (baseMonth: string): void => {}
  const onClickPreviousButton = () => {
    if (isPreviousButtonDisabled()) {
      return
    }

    const newMonth = dayjs(month).add(-1, 'months').format('YYYY-MM')
    setMonth(newMonth)
  }

  const onClickNextButton = () => {
    if (isNextButtonDisabled()) {
      return
    }

    const newMonth = dayjs(month).add(1, 'months').format('YYYY-MM')
    setMonth(newMonth)
  }

  const activityScore = myActivityRewardHistory?.activityScore ?? 0
  const todayActivityScore = myActivityRewardHistory?.todayActivityScore ?? 0

  const activityScoreHeader = (
    <Fragment>
      <SelectMonth isSingleBtn className="activity-score">
        <AdvButton
          width={40}
          height={40}
          ico={<ArrowIco/>}
          icoColor="darkTxt"
          icoRotate={90}
          icoSize={12}
          className={`prev-btn ${isPreviousButtonDisabled() ? 'disabled' : ''}`}
          onClick={onClickPreviousButton}
        />
        <div className="date">
          <span className="year">{dayjs(month).format('YYYY')}</span>
          <span className="month">{dayjs(month).format('M')}월</span>
        </div>
        <AdvButton
          width={40}
          height={40}
          ico={<ArrowIco/>}
          icoColor="darkTxt"
          icoRotate={-90}
          icoSize={12}
          className={`next-btn ${isNextButtonDisabled() ? 'disabled' : ''}`}
          onClick={onClickNextButton}
        />
      </SelectMonth>
    </Fragment>
  )

  const ActivityScoreList = (
    <Fragment>점수 내역<ArrowIco/></Fragment>
  )
  const onClickActivityScoreListPopup = () => {
    PopupUtils.showActivityScoreListPopup()
  }

  const imageUrl = globalStore.getIngameImagesUrl()
  const version = globalStore.getIngameImagesVersion()
  const activityScoreImageUrl = `${imageUrl}guide/activityscore/`

  const makeIconImageUrl = (fileName: string, fileExtension = 'png') => {
    return `${imageUrl}icon/${fileName}.${fileExtension}${version}`
  }
  const makeActiveScoreImageUrl = (fileName: string, fileExtension = 'png') => {
    return `${activityScoreImageUrl}${fileName}.${fileExtension}${version}`
  }

  const globalUserActivityScoreOptions = globalStore.findGlobalSetting(GlobalStoreKey.USER_ACTIVITY_SCORE_OPTIONS).value
  const globalUserPickWrittenScoreOptions = globalStore.findGlobalSetting(
    GlobalStoreKey.USER_PICK_WRITTEN_ACTIVITY_SCORE_OPTION
  ).value
  const userActivityScoreOptions = JSON.parse(globalUserActivityScoreOptions) as UserActivityScoreOption[]
  const userPickWrittenScoreOptions = JSON.parse(
    globalUserPickWrittenScoreOptions
  ) as UserPickWrittenActivityScoreOption[]
  const chatOptions = chattingStore.chattingOptions

  const groupedLiElements = {
    companyPick: [],
    userPick: [],
    chatting: [],
  }

  for (const scoreOption of userActivityScoreOptions) {
    const { activityType, score, dailyLimit } = scoreOption
    let activityCount = 0

    if (activityType === USER_ACTIVITY_TYPE.CHEER_FOR_TEAM && chatOptions?.activeCheerForTeam === false) {
      continue
    }

    activityCount = _.chain(myActivityRewardHistory?.todayUserActivities)
      .filter(userActivity => {
        return userActivity.activityType === activityType
        && userActivity.activityScore > 0
      })
      .size()
      .value()

    const optionsCount = _.size(userPickWrittenScoreOptions)
    const heightValue = optionsCount >= 2 ? `${24 * optionsCount + 10 * (optionsCount - 1)}px` : ''
    const activityScoreListClassName = classNames('activity-score-list', {
      [`user-pick-option --${optionsCount}`]: optionsCount >= 2,
    })

    let liElement: JSX.Element
    if (activityType === USER_ACTIVITY_TYPE.USER_PICK_WRITTEN) {
      liElement = (
        <li
          key={activityType}
          className={activityScoreListClassName}
          style={{ height: `${heightValue}`}}
        >
          <div className="activity-score-user-pick-wrap">
            {_.map(userPickWrittenScoreOptions, (el, index) => {
              return (
                <div>
                  <img src={makeActiveScoreImageUrl(`activity_score_${el.score}`)} alt="score"
                       className={`score ${index}`}/>
                  <p className="activity-type">{`${toUserActivityTypeDesc(activityType)}(${el.limit}자 이상)`}</p>
                </div>
              )
            })}
          </div>
          <span className="dash"/><span className="daily-limit">{activityCount}/{dailyLimit}</span>
        </li>
      )
    } else {
      liElement = (
        <li key={activityType} className="activity-score-list">
          <img src={makeActiveScoreImageUrl(`activity_score_${score}`)} alt="score" className="score"/>
          <p className="activity-type">{toUserActivityTypeDesc(activityType)}</p>
          <span className="dash"/><span className="daily-limit">{activityCount}/{dailyLimit}</span>
        </li>
      )
    }

    switch (activityType) {
      case USER_ACTIVITY_TYPE.COMPANY_PICK_COMMENT:
      case USER_ACTIVITY_TYPE.COMPANY_PICK_LIKED:
        groupedLiElements.companyPick.push(liElement)
        break
      case USER_ACTIVITY_TYPE.USER_PICK_WRITTEN:
      case USER_ACTIVITY_TYPE.TODAY_USER_PICK:
      case USER_ACTIVITY_TYPE.USER_PICK_COMMENT:
      case USER_ACTIVITY_TYPE.USER_PICK_LIKED:
      case USER_ACTIVITY_TYPE.USER_PICK_RECEIVED_COMMENT:
      case USER_ACTIVITY_TYPE.USER_PICK_RECEIVED_LIKE:
        groupedLiElements.userPick.push(liElement)
        break
      case USER_ACTIVITY_TYPE.BET_HISTORY_SHARED_TO_CHAT:
        groupedLiElements.chatting.push(liElement)
        break
      default:
        break
    }
  }

  const activityScoreAcquisitionGuide = (
    <div className="activity-score-acquisition-guide">
      <div className="today-activity-score">
        <img src={makeActiveScoreImageUrl('today_activity_socre_bg')} alt="오늘 획득점수 배경"
             className="today-activity-score-bg"/>
        <p className="today-activity-score-txt">오늘 획득점수 <span className="score">{todayActivityScore}</span>점</p>
      </div>
      <ul className="activity-score company-pick">
        <div className="active-score-title">
          <img src={makeIconImageUrl('premium_analyst_profile')} alt="프리미엄 분석가" className="location"/>
          <img src={makeActiveScoreImageUrl('activity_score_premium_analyst_text')} alt="프리미엄 분석가" className="location-txt"/>
        </div>
        {groupedLiElements.companyPick}
      </ul>
      <ul className="activity-score user-pick">
        <div className="active-score-title">
          <img src={makeIconImageUrl('user_pick_profile')} alt="나도 승부사" className="location"/>
          <img src={makeActiveScoreImageUrl('activity_score_user_pick_text')} alt="나도 승부사" className="location-txt"/>
        </div>
        {groupedLiElements.userPick}
        <p className="sub-notice">
          *본인 명의의 다른 계정으로 점수를 획득할 수 없습니다.<br/>
          *게시물 당 명의 별 최초 1회만 점수에 반영됩니다.
        </p>
      </ul>
      <ul className="activity-score community">
        <div className="active-score-title">
          <img src={makeIconImageUrl('community_profile')} alt="커뮤니티" className="location"/>
          <img src={makeActiveScoreImageUrl('activity_score_community_text')} alt="커뮤니티" className="location-txt"/>
        </div>
        {groupedLiElements.chatting}
        <p className="sub-notice">
          *동일한 베팅내역 공유로 중복 획득할 수 없습니다.
        </p>
      </ul>
      <div className="activity-score-bottom-notice">
        <div className="bottom-notice">
          <p className="notice-title">꼭 확인해주세요!</p>
          <span className="notice-sub-title">이용안내</span>
          <ul>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">본 활동점수 컨텐츠는 <span className="bold">[양질의 커뮤니티 활성화]</span> 취지에 맞게 참여해 주신 베터님께 혜택을 드리고자 마련되었습니다.</span>
            </li>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">취지와는 다른 비매너, 도배, 비연관 글, 무의미한 글 등 본질을 훼손하는 활동은 활동점수 컨텐츠의 존속을 위협하므로 관리됩니다.</span>
            </li>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">부적절하다 판단되는 활동은 점수가 차감되거나 커뮤니티 활동에 제약이 발생할 수 있으므로 유념해 주시길 바랍니다.</span>
            </li>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">승부예측을 위한 정보, 긍정적 소통, 지속적인 혜택 제공을 위해 함께 만들어가는 베터님들의 의미있는 참여를 기대합니다.</span>
            </li>
          </ul>
          <span className="notice-sub-title">유의사항</span>
          <ul>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">점수 획득 횟수는 명의 기준이며, 다계정을 이용해 일일 최대 횟수를 초과할 수 없습니다.</span>
            </li>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">부적절한 게시물, 댓글 작성시 활동 점수가 차감되며, 커뮤니티 활동에 제약이 발생할 수 있습니다.</span>
            </li>
            <li className="bottom-notice-list">
              <span className="bullet">-</span>
              <span className="txt">당사 사정에 의해 내용이 변경되거나 별도 공지후 콘텐츠 제공 기간이 조정될 수 있습니다.</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  )

  return (
    <UserActivityScoreWrap className={!isMobile ? 'pc' : ''}>
      {activityScoreHeader}
      <div className="user-activity-score">
        <p className="user-score">이번달 점수 <span>{activityScore}</span>점</p>
        <AdvButton
          contents={ActivityScoreList}
          onClick={onClickActivityScoreListPopup}
          className="activity-score-list-btn"
        />
      </div>
      <ActivityRewardList activityRewardHistory={myActivityRewardHistory}/>

      {activityScoreAcquisitionGuide}
    </UserActivityScoreWrap>
  )

})
